import React, { useCallback, useState } from "react"
import { IconSparkles, IconUsers } from "@tabler/icons-react"
import type { MinistryGroup } from "../../../lib/chapterContent"
import { chapterSectionContainerClass, chapterSectionContentClass } from "../../../lib/chapters"
import LandingReveal from "../LandingReveal"
import LandingSectionHeader from "../LandingSectionHeader"
import LandingMinistryJoinModal from "../LandingMinistryJoinModal"

type Props = {
  ministries: MinistryGroup[]
}

/** 2-col × 2 rows on small screens; 4-col × 2 rows on desktop. Last slot is the "And more" CTA. */
const MOBILE_VISIBLE = 3
const DESKTOP_VISIBLE = 7

const ChapterMinistriesSection: React.FC<Props> = ({ ministries }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMinistry, setSelectedMinistry] = useState("")
  const ministryNames = ministries.map((m) => m.name)
  const visibleMinistries = ministries.slice(0, DESKTOP_VISIBLE)
  const extraCount = Math.max(0, ministries.length - DESKTOP_VISIBLE)

  const openJoinForm = (ministry = "") => {
    setSelectedMinistry(ministry)
    setModalOpen(true)
  }

  const closeJoinForm = useCallback(() => setModalOpen(false), [])

  return (
    <>
      <section id="ministries" className="scroll-mt-28 bg-[#0d0d0d] py-16 md:py-24">
        <div className={chapterSectionContainerClass}>
          <LandingSectionHeader
            eyebrow="Serve With Us"
            title="Ministry Groups"
            description="Use your gifts to build the house and bless the community."
            className="[&_h2]:text-white [&_p]:text-white/60"
          />

          <div className={`${chapterSectionContentClass} grid grid-cols-2 gap-4 lg:grid-cols-4`}>
            {visibleMinistries.map((ministry, index) => (
              <LandingReveal
                key={ministry.name}
                delay={index * 0.08}
                className={`h-full ${index >= MOBILE_VISIBLE ? "max-lg:hidden" : ""}`}
              >
                <article className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition duration-300 hover:border-[#9e7e43]/40 hover:bg-white/8">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#9e7e43]/15 text-[#9e7e43]">
                    <IconUsers size={20} stroke={1.5} />
                  </div>
                  <h3 className="font-outfit text-lg font-semibold text-white">{ministry.name}</h3>
                  <p className="mt-2 flex-1 font-dm-sans text-sm leading-6 text-white/55">
                    {ministry.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => openJoinForm(ministry.name)}
                    className="mt-4 self-start font-dm-sans text-sm font-semibold text-[#9e7e43] transition group-hover:underline"
                  >
                    Join this ministry →
                  </button>
                </article>
              </LandingReveal>
            ))}

            <LandingReveal delay={visibleMinistries.length * 0.08} className="h-full">
              <button
                type="button"
                onClick={() => openJoinForm()}
                className="group relative flex h-full min-h-[220px] w-full flex-col overflow-hidden rounded-2xl border border-dashed border-[#9e7e43]/55 bg-gradient-to-br from-[#9e7e43]/25 via-[#9e7e43]/8 to-transparent p-6 text-left backdrop-blur-sm transition duration-300 hover:border-[#9e7e43] hover:from-[#9e7e43]/35 hover:via-[#9e7e43]/12"
              >
                <div
                  className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#9e7e43]/20 blur-2xl transition group-hover:bg-[#9e7e43]/30"
                  aria-hidden
                />
                <div className="relative mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#9e7e43] text-white shadow-[0_8px_20px_-8px_rgba(158,126,67,0.8)]">
                  <IconSparkles size={20} stroke={1.5} />
                </div>
                <h3 className="relative font-outfit text-lg font-semibold text-white">And more</h3>
                <p className="relative mt-2 flex-1 font-dm-sans text-sm leading-6 text-white/70">
                  {extraCount > 0
                    ? "Youth, kids, and more teams are serving in the house. Register and we'll help you find your place."
                    : "The house is bigger than this list. Register to serve and we'll help you find your team."}
                </p>
                <span className="relative mt-4 font-dm-sans text-sm font-semibold text-[#e8d5a8] transition group-hover:underline">
                  Find your team →
                </span>
              </button>
            </LandingReveal>
          </div>
        </div>
      </section>

      <LandingMinistryJoinModal
        ministries={ministryNames}
        initialMinistry={selectedMinistry}
        open={modalOpen}
        onClose={closeJoinForm}
      />
    </>
  )
}

export default ChapterMinistriesSection
